import { create } from "zustand";
import JobService from "services/JobService";
import FilterService from "../services/JobService/FilterService";
import JobTabService from "../services/JobService/JobTabService";
import { getNewErrorMessage } from "services/utilsv2";
import Router from "next/router";
import CacheService from "../services/CacheService";

/**
 * Helper function to update URL query parameters with filters
 */
const updateUrlWithFilters = (filters, allFilterOptions = []) => {
  if (typeof window === "undefined") return;

  // Create a copy of the current query params
  const query = { ...Router.query };

  // Get list of filter IDs from allFilterOptions
  const filterKeys = allFilterOptions.map(filter => filter.id);

  // Clear only existing filter params using the IDs from allFilterOptions
  filterKeys.forEach(key => {
    delete query[key];
  });

  // Add filter parameters
  Object.entries(filters).forEach(([filterId, values]) => {
    if (values.length > 0) {
      query[filterId] = values.join(",");
    }
  });

  // Update URL without reloading the page
  Router.replace(
    {
      pathname: Router.pathname,
      query
    },
    undefined,
    { shallow: true }
  );
};

/**
 * Helper function to read filters from URL query parameters
 */
const getFiltersFromUrl = () => {
  if (typeof window === "undefined") return {};

  const query = Router.query;
  const filters = {};

  // Extract filter parameters (ignore tab, page, etc.)
  for (const [key, value] of Object.entries(query)) {
    // Skip non-filter params
    if (
      key !== "tab" &&
      key !== "page" &&
      key !== "sort" &&
      key !== "showMobileFilters" &&
      value
    ) {
      filters[key] = value.split(",");
    }
  }

  return filters;
};

/**
 * Get tab query parameter from URL
 */
const getTabFromUrl = () => {
  if (typeof window === "undefined") return null;

  return Router.query.tab || null;
};

/**
 * Tab store to manage active tab, tab data, pagination, and filters
 *
 * This store handles job data fetching and state management
 */
export const useTabStore = create((set, get) => ({
  // Tab state
  activeTab: null, // Default tab (assuming 'active' is the default)
  tabData: [],
  isLoading: false,
  isInitialLoad: true,
  isFirstTimeFilterLoading: true,
  // Pagination state
  pageNumber: 1,
  pageSize: 10,
  totalPages: null,
  hasMoreData: true,
  isFetchingNextPage: false,
  // Filter API State
  filterOptions: [],
  filterOptionsLoading: false,
  filterOptionsError: null,
  selectedFilters: {}, // The currently applied filters

  // Mobile Filters State
  activeMobileFilters: {}, // Temporary store for mobile filters before applying

  // Jobs data state
  jobs: [],
  jobsLoading: false,
  jobsError: null,
  jobsSuccess: false,
  loadMoreClosedJobs: false,
  loadMoreArchivedJobsPressed: false,
  totalJobs: null,
  setJobs: jobs => set({ jobs }),

  // Tab API implementation
  fetchTabs: async (overrideTab = null, setActiveTab = true) => {
    try {
      set({ isLoading: true });

      const requestedTabId = overrideTab || getTabFromUrl();
      const cacheKey = { endpoint: 'fetchTabs' };
      const cacheName = 'tabsCache';

      // Check cache (this already handles TTL expiry automatically)
      const cachedData = CacheService.checkInCache({
        key: cacheKey,
        cacheName: cacheName
      });

      if (cachedData?.tabs) {
        const { tabs } = cachedData;
        const cachedActiveTabId = tabs.find(tab => tab.is_active)?.tab_id;

        // Condition A: Different tab requested - make new API call
        if (cachedActiveTabId !== requestedTabId) {
          // Fall through to make API call
        } else {
          // Condition B: Same tab requested - use cache (TTL already validated by CacheService)
          set({ tabData: tabs, isLoading: false, isInitialLoad: false });

          if (cachedActiveTabId && cachedActiveTabId !== get().activeTab && setActiveTab) {
            get().setActiveTab(cachedActiveTabId);
          }

          return tabs;
        }
      }

      // Make API call (either no cache, expired cache, or different tab requested)
      const tabsData = await JobTabService.getTabs({
        activeTabId: requestedTabId
      });

      const { tabs } = tabsData;

      // Cache the fresh response
      CacheService.addToCache({
        data: {
          key: cacheKey,
          value: { tabs }
        },
        cacheName: cacheName,
        ttl: 5 / 60 // 5 seconds
      });

      // Update tab data in store
      set({ tabData: tabs, isLoading: false, isInitialLoad: false });

      const activeTabId = tabs.find(tab => tab.is_active)?.tab_id;
      if (activeTabId && activeTabId !== get().activeTab && setActiveTab) {
        get().setActiveTab(activeTabId);
      }

      return tabs;
    } catch (error) {
      set({
        isLoading: false,
        isInitialLoad: false
      });
      return Promise.reject(getNewErrorMessage(error));
    }
  },

  // Initialize filter state from URL
  initializeFiltersFromUrl: () => {
    const urlFilters = getFiltersFromUrl();
    set({ selectedFilters: urlFilters });
  },

  // Actions
  setActiveTab: tabId => {
    set({
      activeTab: tabId
    });

    // Update URL query params with new tab
    if (typeof window !== "undefined") {
      const query = { ...Router.query, tab: tabId };
      Router.replace(
        {
          pathname: Router.pathname,
          query
        },
        undefined,
        { shallow: true }
      );
    }
  },

  setTabData: tabs =>
    set({
      tabData: tabs,
      isLoading: false,
      isInitialLoad: false
    }),

  setLoading: status => set({ isLoading: status }),

  resetInitialLoad: () => set({ isInitialLoad: true }),

  // Pagination actions
  setPageNumber: (pageNumber, callJobs = true) => {
    set({ pageNumber });
    // Refetch jobs when page changes
    if (callJobs) {
      get().fetchJobs();
    }
  },
  setPageSize: pageSize => {
    set({ pageSize });
    // Refetch jobs when page size changes
    get().fetchJobs();
  },
  resetPagination: () =>
    set({
      pageNumber: 1,
      jobs: [],
      hasMoreData: true,
      loadMoreClosedJobs: false,
      loadMoreArchivedJobsPressed: false,
      totalJobs: null,
      jobsSuccess: false,
      jobsLoading: false,
      jobsError: null
    }),
  removeJob: job_id => {
    set(state => {
      const updatedJobs = state.jobs.filter(j => j.job_id !== job_id);
      return { jobs: updatedJobs };
    });
  },

  updateSingleJobLocal: (job_id, job) => {
    set(state => {
      const updatedJobs = state.jobs.map(j =>
        j.job_id === job_id ? { ...job } : j
      );
      return { jobs: updatedJobs };
    });
  },

  updateSingleJob: async job_id => {
    set(state => {
      const updatedJobs = state.jobs.map(j =>
        j.job_id === job_id ? { ...j, isLoading: true } : j
      );
      return { jobs: updatedJobs };
    });

    try {
      const jobData = await JobService.getJobDetails(job_id);

      set(state => {
        const updatedJobs = state.jobs.map(j =>
          j.job_id === job_id ? { ...jobData, isLoading: false } : j
        );
        return { jobs: updatedJobs };
      });
    } catch (error) {
      set(state => {
        const updatedJobs = state.jobs.map(j =>
          j.job_id === job_id ? { ...j, isLoading: false } : j
        );
        return { jobs: updatedJobs };
      });
    }
  },
  // Filter API actions
  fetchFilterOptions: async (customFilters = null) => {
    const { selectedFilters, activeTab } = get();

    if (!activeTab) return;

    try {
      set({ filterOptionsLoading: true, filterOptionsError: null });

      // Use customFilters if provided (for mobile filter preview), otherwise use the filters from state
      const filtersToUse = customFilters || selectedFilters;

      // Call the real filters API with current selected filters and active tab
      const filterOptions = await FilterService.getTabFilters({
        tabId: activeTab,
        selectedFilters: filtersToUse
      });

      set({
        filterOptions,
        filterOptionsLoading: false,
        isFirstTimeFilterLoading: false
      });
    } catch (error) {
      set({
        filterOptionsError: error.message,
        filterOptionsLoading: false,
        isFirstTimeFilterLoading: false
      });
    }
  },

  // Apply the filters - this is called only when the Apply button is clicked
  applyFilters: async newFilters => {
    // Update the selected filters
    set({ selectedFilters: newFilters });

    // Update URL with new filters (no longer passing activeTab)
    updateUrlWithFilters(newFilters, get().filterOptions);
  },
  applyMobileFilters: async newFilters => {
    // Update the selected filters
    set({ selectedFilters: newFilters });
  },

  emptyFiltersState: () => {
    set({ selectedFilters: {}, isFirstTimeFilterLoading: true });
    // Update URL to remove filters
    updateUrlWithFilters({}, get().filterOptions);
  },

  // Clear all filters
  clearFilters: () => {
    get().emptyFiltersState();
  },

  // Mobile filter management
  prepareMobileFilters: (filters = null) => {
    // If filters are provided, use them, otherwise clone the current selectedFilters
    const initialMobileFilters = filters || getFiltersFromUrl();
    set({ activeMobileFilters: initialMobileFilters });
  },
  // Cancel mobile filters - discard changes
  cancelMobileFilters: () => {
    set({ activeMobileFilters: {} });
  },

  // Reset mobile filters - clear all temporary filters
  resetMobileFilters: () => {
    set({ activeMobileFilters: {} });
    // Also update the filter options to reflect no filters
    get().fetchFilterOptions({});
  },

  // Jobs fetching action
  fetchJobs: async () => {
    const {
      activeTab,
      pageNumber,
      pageSize,
      selectedFilters,
      loadMoreClosedJobs
    } = get();

    // Don't fetch if no active tab
    if (!activeTab) return;

    try {
      set({
        jobsLoading: true,
        jobsError: null,
        jobsSuccess: false
      });

      // For real implementation:
      const response = await JobService.getJobList({
        tab: loadMoreClosedJobs ? "close_reject_archived" : activeTab,
        pageNumber,
        pageSize,
        selectedFilters: loadMoreClosedJobs ? {} : selectedFilters
      });

      const {
        jobs = [],
        current_page,
        total_pages,
        total_jobs,
        jobs_per_page,
        load_more
      } = response;
      // Check if this is a fetch for the next page
      const currentJobs =
        pageNumber === 1 && !loadMoreClosedJobs ? [] : get().jobs;

      // Check if there's more data to load - using the API's total_pages
      const hasMoreData = current_page < total_pages;

      set({
        jobs: [...currentJobs, ...jobs],
        jobsLoading: false,
        jobsSuccess: true,
        pageNumber: current_page,
        totalPages: total_pages,
        pageSize: jobs_per_page,
        totalJobs: total_jobs,
        hasMoreData,
        isFetchingNextPage: false,
        loadMoreClosedJobs: load_more
      });
    } catch (error) {
      set({
        jobsError: error.message,
        jobsLoading: false,
        isFetchingNextPage: false,
        jobsSuccess: false
      });
    }
  },

  // New function for infinite scrolling
  fetchNextPage: async () => {
    const { hasMoreData, isFetchingNextPage, pageNumber } = get();

    // Don't fetch if already loading or no more data
    if (isFetchingNextPage || !hasMoreData) return;

    // Set loading state
    set({ isFetchingNextPage: true });

    // Increment page number
    set({ pageNumber: pageNumber + 1 });

    // Fetch next page of jobs
    await get().fetchJobs();
  },

  // Check if we should load more data based on scroll position
  checkAndLoadMore: (scrollPosition, windowHeight, documentHeight) => {
    const { hasMoreData, isFetchingNextPage } = get();

    // Only proceed if there's more data and we're not already fetching
    if (!hasMoreData || isFetchingNextPage) return;

    // Calculate how close we are to the bottom (in pixels)
    const distanceToBottom = documentHeight - (scrollPosition + windowHeight);

    // Load more when within 25% of the viewport height from the bottom
    // This approach automatically adapts to different screen sizes
    const threshold = windowHeight * 0.25;

    if (distanceToBottom < threshold) {
      get().fetchNextPage();
    }
  },

  setLoadMoreArchivedJobsPressed: data => {
    set({ loadMoreArchivedJobsPressed: data });
  },

  // Reset all states to their initial values
  resetAllStates: () => {
    set({
      // Tab state
      activeTab: null,
      tabData: [],
      isLoading: false,
      isInitialLoad: true,
      isFirstTimeFilterLoading: true,

      // Pagination state
      pageNumber: 1,
      pageSize: 10,
      totalPages: null,
      hasMoreData: true,
      isFetchingNextPage: false,

      // Filter states
      filterOptions: [],
      filterOptionsLoading: false,
      filterOptionsError: null,
      selectedFilters: {},
      activeMobileFilters: {},

      // Jobs data state
      jobs: [],
      jobsLoading: false,
      jobsError: null,
      loadMoreClosedJobs: false,
      loadMoreArchivedJobsPressed: false,
      totalJobs: null
    });
  }
}));
